import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import {
  brandingSchema,
  brandingSuggestResponseSchema,
  type Branding,
  type BrandingSuggestResponse,
  type BrandingUpdate,
} from "@msc/shared";

import { api } from "../lib/api.js";
import { customerViewKeys } from "./useCustomerView.js";

/**
 * Kundendesign in der Kundenakte (D10): Website, Logo und zwei Farben je
 * CRM-Kunde. Jede Änderung invalidiert zusätzlich das Bühnen-Design der
 * Customer View — die kennt den Kunden nur über die Kundenabteilung, deshalb
 * das ganze Präfix, nicht ein einzelner Schlüssel.
 */
export const brandingKeys = {
  detail: (customerId: string) => ["branding", customerId] as const,
};

/** Adresse des Logos — same-origin, mit Version, damit ein neues Bild sofort erscheint. */
export function brandingLogoUrl(branding: Pick<Branding, "customerId" | "hasLogo" | "updatedAt">): string | null {
  if (!branding.hasLogo) return null;
  const v = branding.updatedAt ? Date.parse(branding.updatedAt) : 0;
  return `/api/v1/customers/${encodeURIComponent(branding.customerId)}/branding/logo?v=${Number.isNaN(v) ? 0 : v}`;
}

export function useBranding(customerId: string) {
  return useQuery<Branding>({
    queryKey: brandingKeys.detail(customerId),
    queryFn: async () =>
      brandingSchema.parse(await api.get<unknown>(`/customers/${encodeURIComponent(customerId)}/branding`)),
    enabled: customerId !== "",
  });
}

/**
 * Nach jeder Änderung: den neuen Stand übernehmen, wenn die Antwort ihn trägt
 * (PUT/Logo liefern den gespeicherten Datensatz), und in jedem Fall neu laden
 * lassen — auch eine Antwort ohne Rumpf (204) hinterlässt so keinen alten Stand.
 */
function settle(queryClient: QueryClient, customerId: string, response: unknown): void {
  const parsed = brandingSchema.safeParse(response);
  if (parsed.success) queryClient.setQueryData(brandingKeys.detail(customerId), parsed.data);
  void queryClient.invalidateQueries({ queryKey: brandingKeys.detail(customerId) });
  void queryClient.invalidateQueries({ queryKey: customerViewKeys.brandingAll });
}

function base(customerId: string): string {
  return `/customers/${encodeURIComponent(customerId)}/branding`;
}

export function useUpdateBranding(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, BrandingUpdate>({
    mutationFn: (patch) => api.put<unknown>(base(customerId), patch),
    onSuccess: (response) => settle(queryClient, customerId, response),
  });
}

export function useUploadLogo(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, File>({
    mutationFn: (datei) => api.upload<unknown>(`${base(customerId)}/logo`, datei),
    onSuccess: (response) => settle(queryClient, customerId, response),
  });
}

export function useRemoveLogo(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, void>({
    mutationFn: () => api.delete<unknown>(`${base(customerId)}/logo`),
    onSuccess: (response) => settle(queryClient, customerId, response),
  });
}

/** „Auf mindsquare zurücksetzen": löscht den Datensatz — Website, Logo, Farben. */
export function useResetBranding(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, void>({
    mutationFn: () => api.delete<unknown>(base(customerId)),
    onSuccess: (response) => settle(queryClient, customerId, response),
  });
}

/** KI-Vorschlag aus der Website; der Server speichert das Ergebnis sofort (`source = ki`). */
export function useSuggestBranding(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation<BrandingSuggestResponse, Error, void>({
    mutationFn: async () =>
      brandingSuggestResponseSchema.parse(await api.post<unknown>(`${base(customerId)}/vorschlag`)),
    onSuccess: (response) => settle(queryClient, customerId, response.branding),
  });
}
